import {Injectable, inject} from '@angular/core';
import {Router} from '@angular/router';
import {L10nTranslationService} from 'angular-l10n';
import {
    ActionColumn,
    BaseCollectionModel,
    FieldOperator,
    Filter,
    FilterConditionDefinition,
    LogicalOperator,
    Pagination,
    SearchModel,
    TableSettings,
    YascFileUploadAdapterV3,
    YascMappedCrudControllerV3,
    YascOptionItem,
} from '@yasc/yasc-material-components';
import {BehaviorSubject, catchError, defer, firstValueFrom, from, map, Observable, of, shareReplay, tap} from 'rxjs';
import {RestApiService} from '../../services/rest-api.service';
import {RuntimeCacheService} from '../../services/runtime-cache.service';
import {UserController} from '../inventory/user.controller';
import {ProjectMapper} from '../../contracts/v0/crm/project.mapper';
import {PermissionList} from '../../permissions/permission-list';
import {Project} from '../../contracts/v0/crm/project.model';
import {UserDetailsRes} from '../../contracts/v0/cp/user.contract';
import {ProjectRole, TrackerType} from '../../enums';
import {InvoiceDocumentFormat, invoiceDocumentFormatLabels} from '../../enums/invoice-document-format';
import {
    ProjectMinRes,
    ProjectSummaryAggregationRes,
    ProjectSummaryGroupingRes,
    ProjectTrackerRes,
    WorkflowDetailRes,
} from '../../contracts/v0/crm';

@Injectable({providedIn: 'root'})
export class ProjectController extends YascMappedCrudControllerV3<ProjectMapper> {
    private readonly rest = inject(RestApiService);
    private readonly runtimeCache = inject(RuntimeCacheService);
    private readonly userController = inject(UserController);
    private readonly router = inject(Router);
    private readonly translate = inject(L10nTranslationService);

    private readonly customerOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);
    private readonly workflowOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);
    private readonly workflowStatusOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);
    private readonly projectContractOptions$ = new BehaviorSubject<YascOptionItem<number>[]>([]);

    readonly projectSearchLoaded$ = new BehaviorSubject<boolean>(false);

    projects: ProjectMinRes[] = [];
    workflows: WorkflowDetailRes[] = [];
    workflowOptions: YascOptionItem<number>[] = [];
    customers: YascOptionItem<number>[] = [];

    private readonly projectTeamUserOptions: YascOptionItem<number>[] = [];
    private readonly projectBudgetUserOptions: YascOptionItem<number | null>[] = [
        new YascOptionItem<number | null>(
            this.translate.translate('resourceManager_projectStatistics_sharedBudget_label'),
            null,
        ),
    ];
    private readonly projectTeamUsers$ = defer(() => from(this.userController.loadData())).pipe(
        map(users => {
            const options = users.data
                .filter(user => user.isActive !== false)
                .map(user => this.toProjectTeamUserOption(user));
            this.mergeProjectTeamUserOptions(options);
            return options;
        }),
        shareReplay(1),
    );
    private readonly projectBudgetUsers$ = this.projectTeamUsers$.pipe(
        map(options => [this.projectBudgetUserOptions[0], ...options]),
        shareReplay(1),
    );
    private readonly projectOffers$ = defer(() => this.rest.v0.offer.search(
        new SearchModel({pagination: new Pagination({index: 0, size: 500})}),
    )).pipe(
        map(result => (result.data ?? [])
            .filter(offer => offer.status === 'accepted')
            .map(offer => new YascOptionItem<number>(
                [offer.offerNumber, offer.subject].filter(Boolean).join(' · '),
                offer.id,
            ))),
        catchError(() => of([] as YascOptionItem<number>[])),
        shareReplay(1),
    );

    override readonly mapper = new ProjectMapper();
    override readonly api = this.rest.v0.crm.project;
    override readonly fileUploadAdapter: YascFileUploadAdapterV3 = {
        upload: file => this.rest.v0.file.uploadFile(file),
    };

    override readonly fieldOptions = {
        projectCustomers: {
            options$: this.customerOptions$.asObservable(),
            remoteSearch: (query: string) => this.searchCustomerOptions(query),
            remoteSearchDebounceMs: 250,
            remoteSearchMinLength: 1,
            translateNames: false,
        },
        projectWorkflows: {
            options$: this.workflowOptions$.asObservable(),
            translateNames: false,
            allowNonOptions: true,
        },
        projectWorkflowStatuses: {
            options$: this.workflowStatusOptions$.asObservable(),
            translateNames: false,
            allowNonOptions: true,
        },
        projectTeamUsers: {
            options: this.projectTeamUserOptions,
            options$: this.projectTeamUsers$,
            remoteSearch: (query: string) => this.searchUserOptions(query),
            remoteSearchDebounceMs: 250,
            remoteSearchMinLength: 1,
            translateNames: false,
        },
        projectBudgetUsers: {
            options: this.projectBudgetUserOptions,
            options$: this.projectBudgetUsers$,
            remoteSearch: (query: string) => this.searchUserOptions(query),
            remoteSearchDebounceMs: 250,
            remoteSearchMinLength: 1,
            translateNames: false,
        },
        projectOffers: {
            options$: this.projectOffers$,
            translateNames: false,
        },
        projectContracts: {
            options$: this.projectContractOptions$.asObservable(),
            translateNames: false,
            allowNonOptions: false,
        },
        invoiceFormats: {
            options: Object.values(InvoiceDocumentFormat).map(value => new YascOptionItem(invoiceDocumentFormatLabels[value], value)),
            translateNames: false,
            allowNonOptions: false,
        },
        projectRoles: {
            options: Object.keys(ProjectRole)
                .filter(key => Number.isNaN(Number(key)))
                .map(name => new YascOptionItem<number>(name, ProjectRole[name as keyof typeof ProjectRole] as number)),
            translateNames: false,
            allowNonOptions: true,
        },
    };

    override readonly showUndo = true;
    override readonly showRedo = true;
    override readonly showReset = true;
    override readonly undoStackSize = 50;

    override readonly tableSettings = new TableSettings<Project>({
        yascRowClick: false,
        yascInlineEditing: false,
        yascShowFilters: true,
        yascShowIndexCol: true,
        yascLabel: 'general_project',
        yascShowColumnsMenu: true,
        yascResizeColumns: true,
        yascMultiSelect: true,
        yascReorderColumns: true,
        yascGroupingHeaders: true,
        yascActionColumns: [
            ActionColumn.Show,
            {action: ActionColumn.Edit, displayIf: () => this.runtimeCache.hasPermission(PermissionList.ProjectUpdate)},
            {action: ActionColumn.Copy, displayIf: () => this.runtimeCache.hasPermission(PermissionList.ProjectCreate)},
            {action: ActionColumn.Delete, displayIf: () => this.runtimeCache.hasPermission(PermissionList.ProjectDelete)},
        ],
        yascCustomColumns: [
            {
                icon: 'request_quote',
                tooltip: this.translate.translate('crm_project_createOffer_tooltip'),
                displayIf: () => this.runtimeCache.hasPermission(PermissionList.OfferCreate),
                onClick: project => void this.router.navigate(['/fibu/offers'], {
                    queryParams: {create: 'true', projectId: project.id},
                }),
            },
            {
                icon: 'receipt_long',
                tooltip: this.translate.translate('crm_project_createInvoice_tooltip'),
                displayIf: () => this.runtimeCache.hasPermission(PermissionList.InvoiceCreate),
                onClick: project => void this.router.navigate(['/fibu/invoices'], {
                    queryParams: {create: 'true', projectId: project.id, billingBasis: 'projectTime'},
                }),
            },
        ],
    });

    override readonly actionDisplay = {
        show: () => this.runtimeCache.hasPermission(PermissionList.ProjectDetails),
        create: () => this.runtimeCache.hasPermission(PermissionList.ProjectCreate),
        edit: () => this.runtimeCache.hasPermission(PermissionList.ProjectUpdate),
        copy: () => this.runtimeCache.hasPermission(PermissionList.ProjectCreate),
        delete: () => this.runtimeCache.hasPermission(PermissionList.ProjectDelete),
    };

    constructor() {
        super();
        this.loadProjectOptions();
    }

    override loadDetail(row: Project) {
        return this.api.detail(row.id).pipe(
            map(response => {
                const customer = response.customer ?? row.customer;
                this.ensureCustomerOption(customer);
                this.ensureCustomerOption(response.timesheetCustomer);
                this.ensureProjectTeamUserOptions(response.teamMembers ?? []);
                this.ensureProjectBudgetUserOptions(response.budgets ?? []);
                this.ensureProjectContractOptions(response.contracts ?? []);

                return this.mapper.fromDetailResponse({
                    ...response,
                    customer,
                    workflowId: response.workflowId ?? row.workflowId,
                    currentWorkflowStatus: response.currentWorkflowStatus ?? row.currentWorkflowStatus,
                } as ProjectTrackerRes);
            }),
        );
    }

    async loadData(searchModel?: SearchModel): Promise<BaseCollectionModel<ProjectMinRes, ProjectSummaryAggregationRes, ProjectSummaryGroupingRes>> {
        const model = this.resolveInitialSearch(searchModel ?? new SearchModel({
            pagination: new Pagination({
                index: 0,
                size: 150,
            }),
        }));

        try {
            const result = await firstValueFrom(this.rest.v0.crm.project.searchWithSummary(model));
            this.projects = result.data ?? [];
            this.projectSearchLoaded$.next(true);
            this.applyGroupingOptions(result.summary?.groupings);
            return result;
        } catch (error) {
            this.projectSearchLoaded$.next(false);
            throw error;
        }
    }

    private ensureProjectTeamUserOptions(teamMembers: any[]): void {
        const options = teamMembers
            .map(member => member?.user)
            .filter(user => user?.id != null)
            .map(user => this.toProjectTeamUserOption(user as UserDetailsRes));
        this.mergeProjectTeamUserOptions(options);
    }

    private ensureProjectBudgetUserOptions(budgets: any[]): void {
        const options = budgets
            .filter(budget => budget?.userId != null && budget?.user != null)
            .map(budget => {
                const user = budget.user;
                const fullName = [user.givenName, user.lastName]
                    .filter((value: string | undefined) => !!value?.trim())
                    .join(' ')
                    .trim();
                return new YascOptionItem<number>(
                    fullName || user.fullName || user.email || user.userName || `Mitarbeiter ${budget.userId}`,
                    Number(budget.userId),
                );
            });
        this.mergeProjectTeamUserOptions(options);
    }

    private ensureProjectContractOptions(contracts: any[]): void {
        this.projectContractOptions$.next((contracts ?? [])
            .filter(contract => contract?.id != null)
            .map(contract => new YascOptionItem<number>(
                [contract.contractNumber, contract.name].filter(Boolean).join(' · '),
                Number(contract.id),
            )));
    }

    private searchUserOptions(query: string): Observable<YascOptionItem<number>[]> {
        const value = (query ?? '').trim();
        const filters = value
            ? [new Filter({
                operator: LogicalOperator.Or,
                condition: ['userName', 'givenName', 'lastName', 'email'].map(fieldName => new FilterConditionDefinition({
                    fieldName,
                    value,
                    condition: FieldOperator.contains,
                })),
            })]
            : [];

        return this.rest.v0.accounts.search(new SearchModel({
            filters,
            pagination: new Pagination({index: 0, size: 50}),
        })).pipe(
            map(result => {
                const options = result.data
                    .filter(user => user.isActive !== false)
                    .map(user => this.toProjectTeamUserOption(user));
                this.mergeProjectTeamUserOptions(options);
                return options;
            }),
        );
    }

    private toProjectTeamUserOption(user: UserDetailsRes): YascOptionItem<number> {
        const fullName = [user.givenName, user.lastName]
            .filter(value => !!value?.trim())
            .join(' ')
            .trim();

        return new YascOptionItem<number>(
            fullName || user.email || `Mitarbeiter ${user.id}`,
            user.id,
        );
    }

    private mergeProjectTeamUserOptions(options: YascOptionItem<number>[]): void {
        for (const option of options) {
            const index = this.projectTeamUserOptions.findIndex(existing => existing.value === option.value);
            if (index >= 0) this.projectTeamUserOptions[index] = option;
            else this.projectTeamUserOptions.push(option);

            const budgetIndex = this.projectBudgetUserOptions.findIndex(existing => existing.value === option.value);
            if (budgetIndex >= 0) this.projectBudgetUserOptions[budgetIndex] = option;
            else this.projectBudgetUserOptions.push(option);
        }
    }

    private searchCustomerOptions(query: string): Observable<YascOptionItem<number>[]> {
        const value = (query ?? '').trim();
        const filters = value
            ? [new Filter({
                operator: LogicalOperator.And,
                condition: [new FilterConditionDefinition({
                    fieldName: 'name',
                    value,
                    condition: FieldOperator.contains,
                })],
            })]
            : [];

        return this.rest.v0.company.search(new SearchModel({
            filters,
            pagination: new Pagination({index: 0, size: 50}),
        })).pipe(
            map(result => result.data.map(company => new YascOptionItem<number>(company.name, company.id))),
            tap(options => {
                for (const option of options) {
                    if (!this.customers.some(existing => existing.value === option.value)) this.customers.push(option);
                }
                this.customerOptions$.next([...this.customers]);
            }),
        );
    }

    private loadProjectOptions(): void {
        this.rest.v0.company.search().subscribe({
            next: result => {
                this.customers = result.data.map(company => new YascOptionItem<number>(company.name, company.id));
                this.customerOptions$.next(this.customers);
            },
            error: () => {
                this.customers = [];
                this.customerOptions$.next([]);
            },
        });

        this.rest.v0.crm.workflow.search().pipe(
            map(result => result.data.filter(workflow => workflow.type === TrackerType.Project)),
            tap(workflows => this.updateWorkflowOptions(workflows)),
        ).subscribe({
            error: () => {
                this.workflows = [];
                this.workflowOptions = [];
                this.workflowOptions$.next([]);
                this.workflowStatusOptions$.next([]);
            },
        });
    }

    private updateWorkflowOptions(workflows: WorkflowDetailRes[]): void {
        this.workflows = workflows;
        this.workflowOptions = workflows.map(workflow => new YascOptionItem<number>(workflow.name, workflow.id));
        this.workflowOptions$.next(this.workflowOptions);

        this.workflowStatusOptions$.next(
            workflows
                .flatMap(workflow => workflow.states ?? [])
                .filter((status, index, all) => all.findIndex(x => x.id === status.id) === index)
                .map(status => new YascOptionItem<number>(status.name, status.id)),
        );
    }

    private ensureCustomerOption(customer?: {id: number; name: string} | null): void {
        if (!customer || this.customers.some(option => option.value === customer.id)) return;

        this.customers = [new YascOptionItem<number>(customer.name, customer.id), ...this.customers];
        this.customerOptions$.next(this.customers);
    }

    private applyGroupingOptions(grouping?: ProjectSummaryGroupingRes): void {
        if (!grouping) return;

        if (grouping.availableCompanies?.length) {
            this.customers = grouping.availableCompanies.map(company => new YascOptionItem<number>(company.name, company.id));
            this.customerOptions$.next(this.customers);
        }

        if (grouping.availableWorkflows?.length) {
            this.workflowStatusOptions$.next(
                grouping.availableWorkflows.map(status => new YascOptionItem<number>(status.name, status.id)),
            );
        }
    }
}
